import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { classes, style } from 'typestyle'

/* Core ========================================================================================= */
import { addSnippet } from '../../__aeki__/core/snippet-manager'

/* Hooks ======================================================================================== */
import useArray from '../../__aeki__/hooks/use-array'
import useFetch from '../../__aeki__/hooks/use-fetch'
import useValue from '../../__aeki__/hooks/use-value'

/* Components =================================================================================== */
import { Box } from '../../__aeki__/components/box/box.component'
import { Button } from '../../__aeki__/components/button'
import { Snippet, SnippetHead } from '../../__aeki__/modules/layouts/snippet'
import { Text } from '../../__aeki__/components/text/text.component'

/* Styles ======================================================================================= */
import { f18i } from '../../__aeki__/styles/styleset/font-size/f18i'
import { fitWidth } from '../../__aeki__/styles/styleset/fit-width/fitWidth'
import { mr4 } from '../../__aeki__/styles/styleset/margin/mr4'
import { p12 } from '../../__aeki__/styles/styleset/padding/p12'
import { pb4i } from '../../__aeki__/styles/styleset/padding/pb4i'
import { pt4i } from '../../__aeki__/styles/styleset/padding/pt4i'
import { textRight } from '../../__aeki__/styles/styleset/text-right/textRight'
import { w100 } from '../../__aeki__/styles/styleset/width/w100'

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

export default addSnippet('ChildcareProgramProjection', (props: any) => {
  const { row, trigger } = props
  const { rowId } = useParams<any>()

  const today = new Date()
  const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/childcare-program-projection/${rowId}`
  const [data, $data] = useFetch({ count: 0, data: [] }, { url: apiUrl })
  const [index, $index] = useValue(0)
  const [total, $total] = useValue(0)
  const [dayToHoursMap, $dayToHoursMap] = useValue({})
  const [days, $days] = useArray<any>([])

  today.setMonth(today.getMonth() + index)
  const refDate1 = new Date(today)
  refDate1.setMonth(today.getMonth() + 1)
  refDate1.setDate(0)

  useEffect(() => {
    if (data.payload.data.length > 0 && Object.keys(dayToHoursMap).length > 0) {
      const temp: any = []

      for (let i = 1; i <= refDate1.getDate(); i++) {
        const refDate2 = new Date(today)
        refDate2.setDate(1)
        temp.push({ date: i })
      }

      let total = 0

      for (let i = 0; i < temp.length; i++) {
        const refDate = new Date(today)
        refDate.setDate(temp[i].date)

        if (dayToHoursMap[refDate.getDay()]) {
          total +=
            dayToHoursMap[refDate.getDay()] *
            data.payload.count *
            row.body.generalInformation.priceHourly
          temp[i].cost =
            dayToHoursMap[refDate.getDay()] *
            data.payload.count *
            row.body.generalInformation.priceHourly
        }

        $days.set(temp)
        $total.set(total)
      }
    }
  }, [index, data.payload, dayToHoursMap, trigger])

  useEffect(() => {
    if (data.mounted && Object.keys(row).length > 0) {
      $data.get().then(() => {
        Object.keys(row.body.hours).map((key: any) => {
          if (row.body.hours[key].open && row.body.hours[key].close) {
            const refTime1: any = new Date(`${2022}-01-01 ${row.body.hours[key].open}`)
            const refTime2: any = new Date(`${2022}-01-01 ${row.body.hours[key].close}`)

            $dayToHoursMap.set((prev: any) => {
              prev[DAYS.indexOf(key)] = Math.abs(refTime2 - refTime1) / 36e5

              return prev
            })
          }
        })
      })
    }
  }, [data.mounted, row])

  return (
    <Snippet>
      <SnippetHead title={`${MONTH_NAMES[today.getMonth()]} ${today.getFullYear()}`}>
        <Box className={fitWidth} flex baseline="none">
          <Button
            className={mr4}
            icon={<span className={classes('material-icons', f18i)}>keyboard_arrow_left</span>}
            size={'sm'}
            baseline={'surface'}
            onClick={() => {
              $index.set((prev: any) => prev - 1)
            }}
          />

          <Button
            icon={<span className={classes('material-icons', f18i)}>keyboard_arrow_right</span>}
            size={'sm'}
            baseline={'surface'}
            onClick={() => {
              $index.set((prev: any) => prev + 1)
            }}
          />
        </Box>
      </SnippetHead>
      <Box maxHeight={400} scroll>
        <table
          style={{
            width: '100%',
          }}
        >
          <thead
            className={style({
              position: 'sticky',
              top: 0,
              background: 'white',
              zIndex: 2,
              borderCollapse: 'collapse',
            })}
          >
            <td
              className={classes(
                p12,
                style({ verticalAlign: 'middle', minWidth: 60, borderCollapse: 'collapse' }),
              )}
              style={{
                borderBottom: '1px solid rgb(220,220,220)',
              }}
            >
              <Text size="sm" semiBold shrink>
                Day
              </Text>
            </td>
            <td
              className={classes(
                p12,
                style({ verticalAlign: 'middle', width: '100%', borderCollapse: 'collapse' }),
              )}
              style={{
                borderBottom: '1px solid rgb(220,220,220)',
              }}
            >
              <Text
                size="sm"
                semiBold
                shrink
                className={classes(textRight, w100, style({ display: 'inline-block' }))}
              >
                Cost
              </Text>
            </td>
          </thead>
          <tbody>
            {days.map((day: any, key: any) => {
              const refDate = new Date(today)
              refDate.setDate(day)
              return (
                <tr key={key}>
                  <td className={classes(p12, pt4i, pb4i, style({ verticalAlign: 'middle' }))}>
                    <Box>
                      <Text size="sm">{day.date}</Text>
                    </Box>
                  </td>
                  <td className={classes(p12, pt4i, pb4i, style({ verticalAlign: 'middle' }))}>
                    <Box flex alignRight>
                      <Text size="sm">
                        {day.cost
                          ? day.cost.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                          : '-'}
                      </Text>
                    </Box>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <table
          className={style({
            position: 'sticky',
            bottom: 0,
            background: 'white',
            zIndex: 2,
            borderCollapse: 'collapse',
            width: '100%',
          })}
        >
          <tbody>
            <tr>
              <td
                className={classes(
                  p12,
                  style({ verticalAlign: 'middle', minWidth: 60, borderCollapse: 'collapse' }),
                )}
                style={{
                  borderTop: '1px solid rgb(220,220,220)',
                }}
              >
                <Text size="sm" semiBold shrink>
                  Enrolled: {data.payload.count}
                </Text>
              </td>
              <td
                className={classes(
                  p12,
                  style({ verticalAlign: 'middle', borderCollapse: 'collapse' }),
                )}
                style={{
                  borderTop: '1px solid rgb(220,220,220)',
                }}
              >
                <Text
                  size="sm"
                  semiBold
                  shrink
                  className={classes(textRight, w100, style({ display: 'inline-block' }))}
                >
                  Total:{' '}
                  {total
                    ? total.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                    : '-'}
                </Text>
              </td>
            </tr>
          </tbody>
        </table>
      </Box>
    </Snippet>
  )
})
